/**
 * Erreur typée remontée par l'api-client. Reflète la forme normalisée
 * `{ error: { code, message, details? } }` de l'API.
 */
export class ApiClientError extends Error {
    code;
    status;
    details;
    constructor(code, message, status, details) {
        super(message);
        this.code = code;
        this.status = status;
        this.details = details;
        this.name = "ApiClientError";
    }
}
/** Erreur de transport (réseau, parsing JSON, etc.) — pas une erreur API. */
export class ApiTransportError extends Error {
    cause;
    constructor(message, cause) {
        super(message);
        this.cause = cause;
        this.name = "ApiTransportError";
    }
}
class VictorApi {
    opts;
    constructor(opts) {
        this.opts = opts;
    }
    async request(path, init) {
        const url = `${this.opts.baseUrl}${path}`;
        const fetchImpl = this.opts.fetchFn ?? fetch;
        const headers = new Headers(init?.headers);
        headers.set("Accept", "application/json");
        // Content-Type : on pose application/json uniquement pour les body
        // string. Pour FormData/Blob/URLSearchParams, le navigateur définit
        // le bon Content-Type (avec le boundary multipart par ex.).
        if (init?.body &&
            typeof init.body === "string" &&
            !headers.has("Content-Type")) {
            headers.set("Content-Type", "application/json");
        }
        const token = this.opts.getAuthToken?.();
        if (token)
            headers.set("Authorization", `Bearer ${token}`);
        let response;
        try {
            response = await fetchImpl(url, {
                credentials: "include", // cookie refresh httpOnly
                ...init,
                headers,
            });
        }
        catch (cause) {
            throw new ApiTransportError(`Échec réseau sur ${path}`, cause);
        }
        // 204 No Content — pas de body à parser
        if (response.status === 204) {
            return undefined;
        }
        let body;
        try {
            body = await response.json();
        }
        catch (cause) {
            throw new ApiTransportError(`Réponse non-JSON sur ${path}`, cause);
        }
        const apiBody = body;
        if ("error" in apiBody) {
            if (response.status === 401)
                this.opts.onUnauthorized?.();
            if (response.status === 403) {
                this.opts.onForbidden?.(apiBody.error.code, apiBody.error.details);
            }
            throw new ApiClientError(apiBody.error.code, apiBody.error.message, response.status, apiBody.error.details);
        }
        if ("data" in apiBody) {
            return apiBody.data;
        }
        throw new ApiClientError("invalid_response", "Réponse API non conforme (ni data ni error).", response.status);
    }
    health = {
        check: () => this.request("/v1/health"),
    };
    auth = {
        login: (input) => this.request("/v1/auth/login", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        completeMfa: (input) => this.request("/v1/auth/login/mfa", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        refresh: () => this.request("/v1/auth/refresh", { method: "POST" }),
        logout: () => this.request("/v1/auth/logout", { method: "POST" }),
        me: () => this.request("/v1/auth/me"),
        forgot: (input) => this.request("/v1/auth/forgot", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        reset: (input) => this.request("/v1/auth/reset", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        acceptInvitation: (input) => this.request("/v1/auth/invitation/accept", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        changePassword: (input) => this.request("/v1/auth/me/password", {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        mfa: {
            setup: () => this.request("/v1/auth/mfa/setup", { method: "POST" }),
            activate: (input) => this.request("/v1/auth/mfa/activate", {
                method: "POST",
                body: JSON.stringify(input),
            }),
            disable: (input) => this.request("/v1/auth/mfa/disable", {
                method: "POST",
                body: JSON.stringify(input),
            }),
        },
        notifPreferences: {
            list: () => this.request("/v1/auth/me/notif-preferences"),
            update: (kind, input) => this.request(`/v1/auth/me/notif-preferences/${encodeURIComponent(kind)}`, { method: "PUT", body: JSON.stringify(input) }),
        },
        sessions: {
            list: () => this.request("/v1/auth/me/sessions"),
            revoke: (sessionId) => this.request(`/v1/auth/me/sessions/${sessionId}`, {
                method: "DELETE",
            }),
        },
    };
    users = {
        list: (query) => {
            const q = query ? `?q=${encodeURIComponent(query)}` : "";
            return this.request(`/v1/users${q}`);
        },
        create: (input) => this.request("/v1/users", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        listRoles: (userId) => this.request(`/v1/users/${userId}/roles`),
        setRoles: (userId, input) => this.request(`/v1/users/${userId}/roles`, {
            method: "PUT",
            body: JSON.stringify(input),
        }),
    };
    etablissements = {
        list: () => this.request("/v1/etablissements"),
        get: (id) => this.request(`/v1/etablissements/${id}`),
        create: (input) => this.request("/v1/etablissements", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        update: (id, input) => this.request(`/v1/etablissements/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        remove: (id) => this.request(`/v1/etablissements/${id}`, { method: "DELETE" }),
    };
    tenants = {
        me: () => this.request("/v1/tenants/me"),
        setDemoCreneau: (input) => this.request("/v1/tenants/me/demo-creneau", {
            method: "POST",
            body: JSON.stringify(input),
        }),
    };
    branding = {
        get: () => this.request("/v1/tenants/me/branding"),
        update: (input) => this.request("/v1/tenants/me/branding", {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        uploadLogo: (file) => {
            const form = new FormData();
            form.append("file", file);
            // FormData : pas de Content-Type manuel, le navigateur pose le
            // boundary multipart. La méthode request ajoute Content-Type
            // seulement pour les body string.
            return this.request("/v1/tenants/me/branding/logo", {
                method: "POST",
                body: form,
            });
        },
        listParams: () => this.request("/v1/tenants/me/params"),
        getParam: (key, at) => {
            const q = at ? `?at=${encodeURIComponent(at)}` : "";
            return this.request(`/v1/tenants/me/params/${encodeURIComponent(key)}${q}`);
        },
        setParam: (key, input) => this.request(`/v1/tenants/me/params/${encodeURIComponent(key)}`, {
            method: "PUT",
            body: JSON.stringify(input),
        }),
    };
    /**
     * Personnalisation des emails sortants du tenant (Paramètres > Emails).
     * Le corps HTML transite en base64 (corpsHtmlB64) : le WAF bloque le HTML
     * brut dans les corps de requête.
     */
    mailTemplates = {
        list: () => this.request("/v1/tenants/me/mail-templates"),
        update: (key, input) => this.request(`/v1/tenants/me/mail-templates/${encodeURIComponent(key)}`, {
            method: "PUT",
            body: JSON.stringify(input),
        }),
        reset: (key) => this.request(`/v1/tenants/me/mail-templates/${encodeURIComponent(key)}`, {
            method: "DELETE",
        }),
        sendTest: (key, input = {}) => this.request(`/v1/tenants/me/mail-templates/${encodeURIComponent(key)}/send-test`, { method: "POST", body: JSON.stringify(input) }),
    };
    documents = {
        list: (entiteType, entiteId) => {
            const params = new URLSearchParams({ entiteType, entiteId });
            return this.request(`/v1/documents?${params.toString()}`);
        },
        get: (id) => this.request(`/v1/documents/${id}`),
        versions: (id) => this.request(`/v1/documents/${id}/versions`),
        create: (input, file) => {
            const form = new FormData();
            form.append("file", file);
            form.append("entiteType", input.entiteType);
            form.append("entiteId", input.entiteId);
            form.append("libelle", input.libelle);
            return this.request("/v1/documents", { method: "POST", body: form });
        },
        addVersion: (id, file, comment) => {
            const form = new FormData();
            form.append("file", file);
            if (comment)
                form.append("comment", comment);
            return this.request(`/v1/documents/${id}/versions`, {
                method: "POST",
                body: form,
            });
        },
        remove: (id) => this.request(`/v1/documents/${id}`, { method: "DELETE" }),
        /**
         * Download direct du blob (renvoie un Blob, pas un body JSON).
         * Le caller construit ensuite l'URL via URL.createObjectURL pour
         * trigger le téléchargement navigateur, ou pour preview.
         */
        download: async (id, version) => {
            const url = `${this.opts.baseUrl}/v1/documents/${id}/download${version != null ? `?version=${version}` : ""}`;
            const headers = new Headers();
            const token = this.opts.getAuthToken?.();
            if (token)
                headers.set("Authorization", `Bearer ${token}`);
            const fetchImpl = this.opts.fetchFn ?? fetch;
            let response;
            try {
                response = await fetchImpl(url, { credentials: "include", headers });
            }
            catch (cause) {
                throw new ApiTransportError(`Échec réseau sur /documents/${id}/download`, cause);
            }
            if (!response.ok) {
                if (response.status === 401)
                    this.opts.onUnauthorized?.();
                throw new ApiClientError("download_failed", `Téléchargement impossible (${response.status})`, response.status);
            }
            return response.blob();
        },
    };
    /**
     * Recherche globale cross-tables, scopée tenant + permissions (T2.8).
     * GET /v1/search?q=...
     */
    search = {
        global: (q) => this.request(`/v1/search?q=${encodeURIComponent(q)}`),
    };
    /**
     * Journal d'audit (T2.4 — UI platform).
     * Lecture seule : pas de create/update/delete (append-only côté API).
     */
    audit = {
        list: (limit) => {
            const q = limit != null ? `?limit=${limit}` : "";
            return this.request(`/v1/audit${q}`);
        },
        verify: () => this.request("/v1/audit/verify"),
    };
    /**
     * Import / export (T2.6).
     * - imports.etablissementsCsv : upload CSV → rapport synchrone
     * - exports.tenant : retourne le JSON complet du tenant (à streamer côté
     *   UI vers un download)
     */
    imports = {
        etablissementsCsv: (file) => {
            const form = new FormData();
            form.append("file", file);
            return this.request("/v1/imports/etablissements", {
                method: "POST",
                body: form,
            });
        },
    };
    exports = {
        tenant: () => this.request("/v1/exports/tenant"),
    };
    /**
     * Sites client (Vague 1 A.1).
     * Filtres list : `statut` (actif|archive) et `type` (gardiennage|...).
     */
    sites = {
        list: (filters = {}) => {
            const qs = new URLSearchParams();
            if (filters.statut)
                qs.set("statut", filters.statut);
            if (filters.type)
                qs.set("type", filters.type);
            const q = qs.toString();
            return this.request(`/v1/sites${q ? `?${q}` : ""}`);
        },
        get: (id) => this.request(`/v1/sites/${id}`),
        create: (input) => this.request("/v1/sites", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        update: (id, input) => this.request(`/v1/sites/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        /** Archive (soft delete) — pas de DELETE physique. */
        archive: (id) => this.request(`/v1/sites/${id}`, { method: "DELETE" }),
    };
    /**
     * Agents — dossier RH (Vague 1 A.2).
     */
    agents = {
        list: (filters = {}) => {
            const qs = new URLSearchParams();
            if (filters.statut)
                qs.set("statut", filters.statut);
            const q = qs.toString();
            return this.request(`/v1/agents${q ? `?${q}` : ""}`);
        },
        get: (id) => this.request(`/v1/agents/${id}`),
        create: (input) => this.request("/v1/agents", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        update: (id, input) => this.request(`/v1/agents/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        archive: (id) => this.request(`/v1/agents/${id}`, { method: "DELETE" }),
        // S3.a — RIB chiffré (toujours masqué en sortie)
        getRib: (id) => this.request(`/v1/agents/${id}/rib`),
        updateRib: (id, input) => this.request(`/v1/agents/${id}/rib`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        clearRib: (id) => this.request(`/v1/agents/${id}/rib`, { method: "DELETE" }),
    };
    /**
     * Visites médicales (S3.a — Vague RH).
     */
    visitesMedicales = {
        list: (filters = {}) => {
            const qs = new URLSearchParams();
            if (filters.agentId)
                qs.set("agentId", filters.agentId);
            if (filters.validesA)
                qs.set("validesA", filters.validesA);
            if (filters.expirantAvant)
                qs.set("expirantAvant", filters.expirantAvant);
            const q = qs.toString();
            return this.request(`/v1/visites-medicales${q ? `?${q}` : ""}`);
        },
        get: (id) => this.request(`/v1/visites-medicales/${id}`),
        create: (input) => this.request("/v1/visites-medicales", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        update: (id, input) => this.request(`/v1/visites-medicales/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        remove: (id) => this.request(`/v1/visites-medicales/${id}`, { method: "DELETE" }),
    };
    /**
     * Congés & disponibilités (S3.b — Vague RH).
     */
    conges = {
        listDemandes: (filters = {}) => {
            const qs = new URLSearchParams();
            if (filters.agentId)
                qs.set("agentId", filters.agentId);
            if (filters.statut)
                qs.set("statut", filters.statut);
            if (filters.type)
                qs.set("type", filters.type);
            if (filters.from)
                qs.set("from", filters.from);
            if (filters.to)
                qs.set("to", filters.to);
            const q = qs.toString();
            return this.request(`/v1/conges${q ? `?${q}` : ""}`);
        },
        getDemande: (id) => this.request(`/v1/conges/${id}`),
        createDemande: (input) => this.request("/v1/conges", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        updateDemande: (id, input) => this.request(`/v1/conges/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        submitDemande: (id) => this.request(`/v1/conges/${id}/submit`, { method: "POST" }),
        validerDemande: (id, input) => this.request(`/v1/conges/${id}/valider`, {
            method: "POST",
            body: JSON.stringify(input),
        }),
        cancelDemande: (id) => this.request(`/v1/conges/${id}/cancel`, { method: "POST" }),
        // Soldes
        listSoldes: () => this.request("/v1/conges/soldes"),
        listSoldesAgent: (agentId) => this.request(`/v1/conges/soldes/agent/${agentId}`),
        adjustSolde: (input) => this.request("/v1/conges/soldes/adjust", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        setSolde: (input) => this.request("/v1/conges/soldes/set", {
            method: "POST",
            body: JSON.stringify(input),
        }),
    };
    /**
     * Indisponibilités agents (S3.b — créées auto par congés acceptés
     * ou manuellement par RH/Gérant).
     */
    indisponibilites = {
        list: (filters = {}) => {
            const qs = new URLSearchParams();
            if (filters.agentId)
                qs.set("agentId", filters.agentId);
            if (filters.from)
                qs.set("from", filters.from);
            if (filters.to)
                qs.set("to", filters.to);
            const q = qs.toString();
            return this.request(`/v1/indisponibilites${q ? `?${q}` : ""}`);
        },
        get: (id) => this.request(`/v1/indisponibilites/${id}`),
        create: (input) => this.request("/v1/indisponibilites", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        update: (id, input) => this.request(`/v1/indisponibilites/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        remove: (id) => this.request(`/v1/indisponibilites/${id}`, { method: "DELETE" }),
    };
    /**
     * Spécialité cynophile (Vague 1) — chiens, carnet santé, binômes.
     */
    chiens = {
        list: (filters = {}) => {
            const qs = new URLSearchParams();
            if (filters.statut)
                qs.set("statut", filters.statut);
            const q = qs.toString();
            return this.request(`/v1/chiens${q ? `?${q}` : ""}`);
        },
        get: (id) => this.request(`/v1/chiens/${id}`),
        create: (input) => this.request("/v1/chiens", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        update: (id, input) => this.request(`/v1/chiens/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        listCertificats: (chienId) => this.request(`/v1/chiens/${chienId}/certificats`),
        createCertificat: (chienId, input) => this.request(`/v1/chiens/${chienId}/certificats`, {
            method: "POST",
            body: JSON.stringify(input),
        }),
        updateCertificat: (id, input) => this.request(`/v1/chien-certificats/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        deleteCertificat: (id) => this.request(`/v1/chien-certificats/${id}`, { method: "DELETE" }),
    };
    binomes = {
        list: (filters = {}) => {
            const qs = new URLSearchParams();
            if (filters.agentId)
                qs.set("agentId", filters.agentId);
            if (filters.chienId)
                qs.set("chienId", filters.chienId);
            if (filters.statut)
                qs.set("statut", filters.statut);
            const q = qs.toString();
            return this.request(`/v1/binomes-cynophiles${q ? `?${q}` : ""}`);
        },
        get: (id) => this.request(`/v1/binomes-cynophiles/${id}`),
        create: (input) => this.request("/v1/binomes-cynophiles", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        update: (id, input) => this.request(`/v1/binomes-cynophiles/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
    };
    /**
     * Spécialité SSIAP / ERP (Vague 1) — paramétrage ERP, vérifications
     * périodiques (extincteurs, RIA, désenfumage, etc.), rapports incendie.
     */
    ssiap = {
        getSiteErp: (siteId) => this.request(`/v1/sites/${siteId}/erp`),
        upsertSiteErp: (siteId, input) => this.request(`/v1/sites/${siteId}/erp`, {
            method: "PUT",
            body: JSON.stringify(input),
        }),
    };
    verifications = {
        list: (filters = {}) => {
            const qs = new URLSearchParams();
            if (filters.siteId)
                qs.set("siteId", filters.siteId);
            if (filters.statut)
                qs.set("statut", filters.statut);
            if (filters.from)
                qs.set("from", filters.from);
            if (filters.to)
                qs.set("to", filters.to);
            const q = qs.toString();
            return this.request(`/v1/verifications${q ? `?${q}` : ""}`);
        },
        get: (id) => this.request(`/v1/verifications/${id}`),
        create: (input) => this.request("/v1/verifications", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        update: (id, input) => this.request(`/v1/verifications/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        remove: (id) => this.request(`/v1/verifications/${id}`, { method: "DELETE" }),
    };
    rapportsIncendie = {
        list: (filters = {}) => {
            const qs = new URLSearchParams();
            if (filters.siteId)
                qs.set("siteId", filters.siteId);
            if (filters.statut)
                qs.set("statut", filters.statut);
            if (filters.from)
                qs.set("from", filters.from);
            if (filters.to)
                qs.set("to", filters.to);
            const q = qs.toString();
            return this.request(`/v1/rapports-incendie${q ? `?${q}` : ""}`);
        },
        get: (id) => this.request(`/v1/rapports-incendie/${id}`),
        create: (input) => this.request("/v1/rapports-incendie", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        update: (id, input) => this.request(`/v1/rapports-incendie/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        remove: (id) => this.request(`/v1/rapports-incendie/${id}`, { method: "DELETE" }),
    };
    /**
     * Spécialité télésurveillance (Vague 1) — alarmes entrantes +
     * interventions avec chronologie et SLA.
     */
    alarmes = {
        list: (filters = {}) => {
            const qs = new URLSearchParams();
            if (filters.siteId)
                qs.set("siteId", filters.siteId);
            if (filters.statut)
                qs.set("statut", filters.statut);
            if (filters.type)
                qs.set("type", filters.type);
            if (filters.niveau)
                qs.set("niveau", filters.niveau);
            if (filters.from)
                qs.set("from", filters.from);
            if (filters.to)
                qs.set("to", filters.to);
            const q = qs.toString();
            return this.request(`/v1/alarmes${q ? `?${q}` : ""}`);
        },
        get: (id) => this.request(`/v1/alarmes/${id}`),
        create: (input) => this.request("/v1/alarmes", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        update: (id, input) => this.request(`/v1/alarmes/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        remove: (id) => this.request(`/v1/alarmes/${id}`, { method: "DELETE" }),
    };
    interventions = {
        list: (filters = {}) => {
            const qs = new URLSearchParams();
            if (filters.siteId)
                qs.set("siteId", filters.siteId);
            if (filters.agentAffecteId)
                qs.set("agentAffecteId", filters.agentAffecteId);
            if (filters.statut)
                qs.set("statut", filters.statut);
            if (filters.from)
                qs.set("from", filters.from);
            if (filters.to)
                qs.set("to", filters.to);
            const q = qs.toString();
            return this.request(`/v1/interventions${q ? `?${q}` : ""}`);
        },
        get: (id) => this.request(`/v1/interventions/${id}`),
        create: (input) => this.request("/v1/interventions", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        update: (id, input) => this.request(`/v1/interventions/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        remove: (id) => this.request(`/v1/interventions/${id}`, { method: "DELETE" }),
    };
    /**
     * Spécialité événementiel (Vague 1) — événements + besoins + candidatures.
     */
    evenements = {
        list: (filters = {}) => {
            const qs = new URLSearchParams();
            if (filters.statut)
                qs.set("statut", filters.statut);
            if (filters.clientId)
                qs.set("clientId", filters.clientId);
            if (filters.from)
                qs.set("from", filters.from);
            if (filters.to)
                qs.set("to", filters.to);
            const q = qs.toString();
            return this.request(`/v1/evenements${q ? `?${q}` : ""}`);
        },
        get: (id) => this.request(`/v1/evenements/${id}`),
        create: (input) => this.request("/v1/evenements", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        update: (id, input) => this.request(`/v1/evenements/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        remove: (id) => this.request(`/v1/evenements/${id}`, { method: "DELETE" }),
        // Besoins (nested)
        listBesoins: (evenementId) => this.request(`/v1/evenements/${evenementId}/besoins`),
        createBesoin: (evenementId, input) => this.request(`/v1/evenements/${evenementId}/besoins`, {
            method: "POST",
            body: JSON.stringify(input),
        }),
        updateBesoin: (id, input) => this.request(`/v1/besoins-evenement/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        removeBesoin: (id) => this.request(`/v1/besoins-evenement/${id}`, { method: "DELETE" }),
    };
    candidaturesEvenement = {
        list: (filters = {}) => {
            const qs = new URLSearchParams();
            if (filters.evenementId)
                qs.set("evenementId", filters.evenementId);
            if (filters.agentId)
                qs.set("agentId", filters.agentId);
            if (filters.statut)
                qs.set("statut", filters.statut);
            const q = qs.toString();
            return this.request(`/v1/candidatures-evenement${q ? `?${q}` : ""}`);
        },
        create: (input) => this.request("/v1/candidatures-evenement", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        update: (id, input) => this.request(`/v1/candidatures-evenement/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        remove: (id) => this.request(`/v1/candidatures-evenement/${id}`, { method: "DELETE" }),
    };
    /**
     * Sous-traitance (B.1) — sociétés externes + intervenants rattachés.
     */
    sousTraitants = {
        list: (filters = {}) => {
            const qs = new URLSearchParams();
            if (filters.statut)
                qs.set("statut", filters.statut);
            if (filters.search)
                qs.set("search", filters.search);
            const q = qs.toString();
            return this.request(`/v1/sous-traitants${q ? `?${q}` : ""}`);
        },
        get: (id) => this.request(`/v1/sous-traitants/${id}`),
        create: (input) => this.request("/v1/sous-traitants", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        update: (id, input) => this.request(`/v1/sous-traitants/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        remove: (id) => this.request(`/v1/sous-traitants/${id}`, { method: "DELETE" }),
    };
    intervenantsExternes = {
        list: (filters = {}) => {
            const qs = new URLSearchParams();
            if (filters.sousTraitantId)
                qs.set("sousTraitantId", filters.sousTraitantId);
            if (filters.statut)
                qs.set("statut", filters.statut);
            if (filters.specialite)
                qs.set("specialite", filters.specialite);
            if (filters.search)
                qs.set("search", filters.search);
            const q = qs.toString();
            return this.request(`/v1/intervenants-externes${q ? `?${q}` : ""}`);
        },
        get: (id) => this.request(`/v1/intervenants-externes/${id}`),
        create: (input) => this.request("/v1/intervenants-externes", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        update: (id, input) => this.request(`/v1/intervenants-externes/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        remove: (id) => this.request(`/v1/intervenants-externes/${id}`, { method: "DELETE" }),
    };
    /**
     * Matériel & dotations (B.2) — catalogue articles + suivi sorties/retours.
     */
    articlesMateriel = {
        list: (filters = {}) => {
            const qs = new URLSearchParams();
            if (filters.categorie)
                qs.set("categorie", filters.categorie);
            if (filters.archive !== undefined)
                qs.set("archive", String(filters.archive));
            if (filters.search)
                qs.set("search", filters.search);
            if (filters.stockBas)
                qs.set("stockBas", "true");
            const q = qs.toString();
            return this.request(`/v1/articles-materiel${q ? `?${q}` : ""}`);
        },
        get: (id) => this.request(`/v1/articles-materiel/${id}`),
        create: (input) => this.request("/v1/articles-materiel", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        update: (id, input) => this.request(`/v1/articles-materiel/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        remove: (id) => this.request(`/v1/articles-materiel/${id}`, { method: "DELETE" }),
    };
    dotations = {
        list: (filters = {}) => {
            const qs = new URLSearchParams();
            if (filters.agentId)
                qs.set("agentId", filters.agentId);
            if (filters.articleId)
                qs.set("articleId", filters.articleId);
            if (filters.statut)
                qs.set("statut", filters.statut);
            if (filters.from)
                qs.set("from", filters.from);
            if (filters.to)
                qs.set("to", filters.to);
            const q = qs.toString();
            return this.request(`/v1/dotations${q ? `?${q}` : ""}`);
        },
        get: (id) => this.request(`/v1/dotations/${id}`),
        create: (input) => this.request("/v1/dotations", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        update: (id, input) => this.request(`/v1/dotations/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        remove: (id) => this.request(`/v1/dotations/${id}`, { method: "DELETE" }),
    };
    /**
     * Reporting / analytics (Option C) — agrégats à la volée + CSV.
     */
    reporting = {
        kpis: (periode) => {
            const qs = new URLSearchParams({ from: periode.from, to: periode.to });
            return this.request(`/v1/reporting/kpis?${qs.toString()}`);
        },
        topClients: (periode, limit = 10) => {
            const qs = new URLSearchParams({
                from: periode.from,
                to: periode.to,
                limit: String(limit),
            });
            return this.request(`/v1/reporting/top-clients?${qs.toString()}`);
        },
        topSites: (periode, limit = 10) => {
            const qs = new URLSearchParams({
                from: periode.from,
                to: periode.to,
                limit: String(limit),
            });
            return this.request(`/v1/reporting/top-sites?${qs.toString()}`);
        },
        topAgents: (periode, limit = 10) => {
            const qs = new URLSearchParams({
                from: periode.from,
                to: periode.to,
                limit: String(limit),
            });
            return this.request(`/v1/reporting/top-agents?${qs.toString()}`);
        },
        exportVacationsUrl: (periode) => {
            const qs = new URLSearchParams({ from: periode.from, to: periode.to });
            return `${this.opts.baseUrl}/v1/reporting/exports/vacations.csv?${qs.toString()}`;
        },
        exportFacturesUrl: (periode) => {
            const qs = new URLSearchParams({ from: periode.from, to: periode.to });
            return `${this.opts.baseUrl}/v1/reporting/exports/factures.csv?${qs.toString()}`;
        },
    };
    /**
     * Pipeline commercial (Option D.1) — opportunités.
     */
    opportunites = {
        list: (filters = {}) => {
            const qs = new URLSearchParams();
            if (filters.etape)
                qs.set("etape", filters.etape);
            if (filters.ownerId)
                qs.set("ownerId", filters.ownerId);
            if (filters.clientId)
                qs.set("clientId", filters.clientId);
            if (filters.search)
                qs.set("search", filters.search);
            const q = qs.toString();
            return this.request(`/v1/opportunites${q ? `?${q}` : ""}`);
        },
        get: (id) => this.request(`/v1/opportunites/${id}`),
        create: (input) => this.request("/v1/opportunites", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        update: (id, input) => this.request(`/v1/opportunites/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        remove: (id) => this.request(`/v1/opportunites/${id}`, { method: "DELETE" }),
    };
    /**
     * Facturation récurrente (Option D.3).
     */
    factRecurrente = {
        list: () => this.request("/v1/fact-recurrente"),
        getByContrat: (contratId) => this.request(`/v1/fact-recurrente/contrat/${contratId}`),
        upsert: (input) => this.request("/v1/fact-recurrente", {
            method: "PUT",
            body: JSON.stringify(input),
        }),
        remove: (contratId) => this.request(`/v1/fact-recurrente/contrat/${contratId}`, {
            method: "DELETE",
        }),
    };
    /**
     * Web Push (Option E.1) — abonnement / désabonnement self.
     */
    push = {
        publicConfig: () => this.request("/v1/push/config"),
        listMine: () => this.request("/v1/me/push/subscriptions"),
        subscribe: (input) => this.request("/v1/me/push/subscribe", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        unsubscribe: (endpoint) => this.request("/v1/me/push/subscribe", {
            method: "DELETE",
            body: JSON.stringify({ endpoint }),
        }),
    };
    /**
     * Intégrations (Option G) — statut des connecteurs externes.
     */
    integrations = {
        status: () => this.request("/v1/integrations/status"),
    };
    /**
     * Abonnement SaaS Victor (Stripe Billing) — souscription, portail,
     * statut courant.
     */
    abonnementSaas = {
        me: () => this.request("/v1/abonnement-saas/me"),
        checkout: (input) => this.request("/v1/abonnement-saas/checkout", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        portal: () => this.request("/v1/abonnement-saas/portal", { method: "POST" }),
        gcMandate: (input) => this.request("/v1/abonnement-saas/gc-mandate", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        gcConfirm: (input) => this.request("/v1/abonnement-saas/gc-confirm", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        changePlan: (input) => this.request("/v1/abonnement-saas/change-plan", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        cancelScheduled: () => this.request("/v1/abonnement-saas/cancel-scheduled", { method: "POST" }),
        payNow: (via = "auto") => this.request("/v1/abonnement-saas/pay-now", {
            method: "POST",
            body: JSON.stringify({ via }),
        }),
        getPaymentMethods: () => this.request("/v1/abonnement-saas/payment-methods"),
        setupBackupCard: () => this.request("/v1/abonnement-saas/backup-card", { method: "POST" }),
    };
    /**
     * Contrats SaaS Hubblot ↔ Tenant (souscription, avenants, résiliation).
     * Distinct du namespace `contrats` qui concerne les contrats métier
     * tenant ↔ client donneur d'ordre.
     */
    meContrats = {
        list: () => this.request("/v1/me/contrats"),
        get: (id) => this.request(`/v1/me/contrats/${id}`),
        pdfUrl: (id) => `${this.opts.baseUrl}/v1/me/contrats/${id}/pdf`,
        resilier: (input) => this.request("/v1/me/contrats/resilier", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        annulerResiliation: () => this.request("/v1/me/contrats/annuler-resiliation", {
            method: "POST",
        }),
        signatureStatus: () => this.request("/v1/me/contrats/signature-status"),
        /** Aperçu du contrat à lire avant signature. */
        signPreview: () => this.request("/v1/me/contrats/sign-preview"),
        /** URL du PDF « projet » (à fetch avec le token Bearer puis blob). */
        signPreviewPdfUrl: () => `${this.opts.baseUrl}/v1/me/contrats/sign-preview-pdf`,
        /** Étape 1 signature : envoi du code OTP par SMS (repli e-mail si échec). */
        sendSignCode: (phone) => this.request("/v1/me/contrats/sign/send-code", {
            method: "POST",
            body: JSON.stringify({ phone }),
        }),
        /** Étape 2 signature : vérifie le code OTP → contrat signé. */
        signWithCode: (phone, code) => this.request("/v1/me/contrats/sign/verify", {
            method: "POST",
            body: JSON.stringify({ phone, code }),
        }),
    };
    /** Factures Hubblot reçues par le tenant (e-invoicing B2Brouter). */
    meFacturesHubblot = {
        list: () => this.request("/v1/me/factures-hubblot"),
        pdfUrl: (id) => `${this.opts.baseUrl}/v1/me/factures-hubblot/${id}/pdf`,
    };
    /** Profil émetteur (facturation électronique de l'agence). */
    emetteurProfile = {
        get: () => this.request("/v1/me/emetteur-profile"),
        update: (input) => this.request("/v1/me/emetteur-profile", {
            method: "PUT",
            body: JSON.stringify(input),
        }),
    };
    /**
     * Boutique (Option B+) — packs SMS / agents + extensions admins.
     */
    boutique = {
        listPacks: () => this.request("/v1/boutique/packs"),
        status: () => this.request("/v1/boutique/status"),
        checkout: (input) => this.request("/v1/boutique/checkout", {
            method: "POST",
            body: JSON.stringify(input),
        }),
    };
    /** Prestations Hubblot (identité visuelle, site web) : demande + RDV. */
    services = {
        demandeRendezVous: (input) => this.request("/v1/services/demande-rendez-vous", {
            method: "POST",
            body: JSON.stringify(input),
        }),
    };
    /** Centre d'aide (lecture + recherche plein-texte gratuite). */
    help = {
        tree: () => this.request("/v1/help/tree"),
        search: (q) => this.request(`/v1/help/search?q=${encodeURIComponent(q)}`),
        article: (slug) => this.request(`/v1/help/articles/${encodeURIComponent(slug)}`),
        assistant: (question) => this.request("/v1/help/assistant", {
            method: "POST",
            body: JSON.stringify({ question }),
        }),
        feedback: (slug, helpful) => this.request(`/v1/help/articles/${encodeURIComponent(slug)}/feedback`, {
            method: "POST",
            body: JSON.stringify({ helpful }),
        }),
    };
    /** Tickets de support (agence). */
    support = {
        list: () => this.request("/v1/support/tickets"),
        get: (id) => this.request(`/v1/support/tickets/${id}`),
        create: (input) => this.request("/v1/support/tickets", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        message: (id, body, attachments) => this.request(`/v1/support/tickets/${id}/messages`, {
            method: "POST",
            body: JSON.stringify({ body, attachments }),
        }),
        uploadAttachment: async (file) => {
            const fd = new FormData();
            fd.append("file", file);
            const token = this.opts.getAuthToken?.();
            const res = await (this.opts.fetchFn ?? fetch)(`${this.opts.baseUrl}/v1/support/tickets/upload`, {
                method: "POST",
                body: fd,
                credentials: "include",
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!res.ok)
                throw new Error("upload_failed");
            return (await res.json()).data;
        },
        downloadAttachment: async (key) => {
            const token = this.opts.getAuthToken?.();
            const res = await (this.opts.fetchFn ?? fetch)(`${this.opts.baseUrl}/v1/support/tickets/attachments/file?key=${encodeURIComponent(key)}`, {
                credentials: "include",
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!res.ok)
                throw new Error("download_failed");
            return res.blob();
        },
        close: (id) => this.request(`/v1/support/tickets/${id}/close`, { method: "POST" }),
        reopen: (id) => this.request(`/v1/support/tickets/${id}/reopen`, { method: "POST" }),
        csat: (id, score, comment) => this.request(`/v1/support/tickets/${id}/csat`, {
            method: "POST",
            body: JSON.stringify({ score, comment }),
        }),
    };
    /**
     * Rapports d'incident (Option X) — formels + signature MFA gérant.
     */
    rapportsIncident = {
        list: (filters = {}) => {
            const qs = new URLSearchParams();
            if (filters.statut)
                qs.set("statut", filters.statut);
            if (filters.gravite)
                qs.set("gravite", filters.gravite);
            if (filters.siteId)
                qs.set("siteId", filters.siteId);
            if (filters.from)
                qs.set("from", filters.from);
            if (filters.to)
                qs.set("to", filters.to);
            const q = qs.toString();
            return this.request(`/v1/rapports-incident${q ? `?${q}` : ""}`);
        },
        get: (id) => this.request(`/v1/rapports-incident/${id}`),
        create: (input) => this.request("/v1/rapports-incident", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        update: (id, input) => this.request(`/v1/rapports-incident/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        remove: (id) => this.request(`/v1/rapports-incident/${id}`, { method: "DELETE" }),
        validate: (id, input) => this.request(`/v1/rapports-incident/${id}/validate`, {
            method: "POST",
            body: JSON.stringify(input),
        }),
    };
    /**
     * Webhooks sortants (Option Y).
     */
    webhooks = {
        listEndpoints: () => this.request("/v1/webhooks/endpoints"),
        createEndpoint: (input) => this.request("/v1/webhooks/endpoints", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        updateEndpoint: (id, input) => this.request(`/v1/webhooks/endpoints/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        removeEndpoint: (id) => this.request(`/v1/webhooks/endpoints/${id}`, { method: "DELETE" }),
        getSecret: (id) => this.request(`/v1/webhooks/endpoints/${id}/secret`),
        listDeliveries: (endpointId) => {
            const qs = endpointId
                ? `?${new URLSearchParams({ endpointId }).toString()}`
                : "";
            return this.request(`/v1/webhooks/deliveries${qs}`);
        },
    };
    /**
     * Anomalies (Option Z) — détectées par cron + workflow validation.
     */
    anomalies = {
        list: (filters = {}) => {
            const qs = new URLSearchParams();
            if (filters.statut)
                qs.set("statut", filters.statut);
            if (filters.kind)
                qs.set("kind", filters.kind);
            if (filters.agentId)
                qs.set("agentId", filters.agentId);
            if (filters.from)
                qs.set("from", filters.from);
            if (filters.to)
                qs.set("to", filters.to);
            const q = qs.toString();
            return this.request(`/v1/anomalies${q ? `?${q}` : ""}`);
        },
        validate: (id, input) => this.request(`/v1/anomalies/${id}/validate`, {
            method: "POST",
            body: JSON.stringify(input),
        }),
        confirmSelf: (id) => this.request(`/v1/anomalies/${id}/confirm-self`, { method: "POST" }),
    };
    /**
     * Astreintes (Option AA) — planning d'astreinte + déclenchement.
     */
    astreintes = {
        list: (filters = {}) => {
            const qs = new URLSearchParams();
            if (filters.statut)
                qs.set("statut", filters.statut);
            if (filters.agentId)
                qs.set("agentId", filters.agentId);
            if (filters.from)
                qs.set("from", filters.from);
            if (filters.to)
                qs.set("to", filters.to);
            const q = qs.toString();
            return this.request(`/v1/astreintes${q ? `?${q}` : ""}`);
        },
        create: (input) => this.request("/v1/astreintes", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        update: (id, input) => this.request(`/v1/astreintes/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        remove: (id) => this.request(`/v1/astreintes/${id}`, { method: "DELETE" }),
        trigger: (id, input) => this.request(`/v1/astreintes/${id}/trigger`, {
            method: "POST",
            body: JSON.stringify(input),
        }),
    };
    /**
     * Items sensibles (Option BB) — armes + matériel CNAPS traçabilité.
     */
    itemsSensibles = {
        list: (filters = {}) => {
            const qs = new URLSearchParams();
            if (filters.categorie)
                qs.set("categorie", filters.categorie);
            if (filters.statut)
                qs.set("statut", filters.statut);
            if (filters.search)
                qs.set("search", filters.search);
            const q = qs.toString();
            return this.request(`/v1/items-sensibles${q ? `?${q}` : ""}`);
        },
        get: (id) => this.request(`/v1/items-sensibles/${id}`),
        attributions: (id) => this.request(`/v1/items-sensibles/${id}/attributions`),
        create: (input) => this.request("/v1/items-sensibles", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        update: (id, input) => this.request(`/v1/items-sensibles/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        remove: (id) => this.request(`/v1/items-sensibles/${id}`, { method: "DELETE" }),
        attribuer: (input) => this.request("/v1/items-sensibles/attribuer", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        restituer: (input) => this.request("/v1/items-sensibles/restituer", {
            method: "POST",
            body: JSON.stringify(input),
        }),
    };
    /**
     * Visites de contrôle (Option CC) — CNAPS / préfecture.
     */
    visitesControle = {
        list: (filters = {}) => {
            const qs = new URLSearchParams();
            if (filters.statut)
                qs.set("statut", filters.statut);
            if (filters.type)
                qs.set("type", filters.type);
            if (filters.from)
                qs.set("from", filters.from);
            if (filters.to)
                qs.set("to", filters.to);
            const q = qs.toString();
            return this.request(`/v1/visites-controle${q ? `?${q}` : ""}`);
        },
        get: (id) => this.request(`/v1/visites-controle/${id}`),
        create: (input) => this.request("/v1/visites-controle", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        update: (id, input) => this.request(`/v1/visites-controle/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        remove: (id) => this.request(`/v1/visites-controle/${id}`, { method: "DELETE" }),
    };
    /**
     * Templates planning récurrent (Option N).
     */
    planningTemplates = {
        list: () => this.request("/v1/planning-templates"),
        get: (id) => this.request(`/v1/planning-templates/${id}`),
        create: (input) => this.request("/v1/planning-templates", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        update: (id, input) => this.request(`/v1/planning-templates/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        remove: (id) => this.request(`/v1/planning-templates/${id}`, { method: "DELETE" }),
        generate: (input) => this.request("/v1/planning-templates/generate", {
            method: "POST",
            body: JSON.stringify(input),
        }),
    };
    /**
     * Notifications in-app (Option O) — cloche topbar.
     */
    inAppNotifications = {
        list: (unreadOnly = false) => this.request(`/v1/me/notifications${unreadOnly ? "?unread=1" : ""}`),
        unreadCount: () => this.request("/v1/me/notifications/unread-count"),
        markAllRead: () => this.request("/v1/me/notifications/read-all", { method: "POST" }),
        markOneRead: (id) => this.request(`/v1/me/notifications/${id}/read`, { method: "POST" }),
    };
    /**
     * Messagerie interne (Option P).
     */
    messagerie = {
        listConversations: (filters = {}) => {
            const qs = new URLSearchParams();
            if (filters.vacationId)
                qs.set("vacationId", filters.vacationId);
            if (filters.contratId)
                qs.set("contratId", filters.contratId);
            const q = qs.toString();
            return this.request(`/v1/messagerie/conversations${q ? `?${q}` : ""}`);
        },
        getConversation: (id) => this.request(`/v1/messagerie/conversations/${id}`),
        listMessages: (conversationId) => this.request(`/v1/messagerie/conversations/${conversationId}/messages`),
        createConversation: (input) => this.request("/v1/messagerie/conversations", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        sendMessage: (input) => this.request("/v1/messagerie/messages", {
            method: "POST",
            body: JSON.stringify(input),
        }),
    };
    /**
     * Recrutement / ATS (Option S).
     */
    candidatures = {
        list: (filters = {}) => {
            const qs = new URLSearchParams();
            if (filters.etape)
                qs.set("etape", filters.etape);
            if (filters.search)
                qs.set("search", filters.search);
            const q = qs.toString();
            return this.request(`/v1/candidatures${q ? `?${q}` : ""}`);
        },
        get: (id) => this.request(`/v1/candidatures/${id}`),
        create: (input) => this.request("/v1/candidatures", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        update: (id, input) => this.request(`/v1/candidatures/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        remove: (id) => this.request(`/v1/candidatures/${id}`, { method: "DELETE" }),
        convert: (id) => this.request(`/v1/candidatures/${id}/convert`, { method: "POST" }),
    };
    /**
     * Carte temps réel (Option W).
     */
    carte = {
        snapshot: () => this.request("/v1/carte/snapshot"),
    };
    /**
     * Prépaie mensuelle (S3.c) — calcul agrégé à la volée + exports CSV.
     */
    prepaie = {
        computeMois: (mois) => this.request(`/v1/prepaie?mois=${encodeURIComponent(mois)}`),
        /**
         * Télécharge le CSV (Silae ou PayFit) sous forme de Blob.
         * Le côté front fait `URL.createObjectURL(blob)` + download.
         */
        exportCsv: async (mois, format) => {
            const url = `${this.opts.baseUrl}/v1/prepaie/export?mois=${encodeURIComponent(mois)}&format=${format}`;
            const token = this.opts.getAuthToken?.();
            const fetchImpl = this.opts.fetchFn ?? fetch;
            const res = await fetchImpl(url, {
                credentials: "include",
                headers: {
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    Accept: "text/csv",
                },
            });
            if (!res.ok) {
                throw new ApiClientError("export_failed", "Export CSV impossible.", res.status);
            }
            return res.blob();
        },
    };
    /**
     * Qualifications agents (Vague 1 A.3).
     * Nested sous /agents/:agentId/qualifications pour list/create,
     * accès direct par id pour update/delete.
     */
    qualifications = {
        listForAgent: (agentId) => this.request(`/v1/agents/${agentId}/qualifications`),
        create: (agentId, input) => this.request(`/v1/agents/${agentId}/qualifications`, {
            method: "POST",
            body: JSON.stringify(input),
        }),
        get: (id) => this.request(`/v1/qualifications/${id}`),
        update: (id, input) => this.request(`/v1/qualifications/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        remove: (id) => this.request(`/v1/qualifications/${id}`, { method: "DELETE" }),
        /** Dashboard alertes : qualifs expirant dans `days` jours (défaut 30). */
        expiring: (days = 30) => this.request(`/v1/qualifications/expiring?days=${days}`),
    };
    /**
     * Clients donneurs d'ordre (Vague 1 A.4).
     * Entité qui paye la facture, propriétaire des sites.
     */
    clients = {
        list: (filters = {}) => {
            const qs = new URLSearchParams();
            if (filters.statut)
                qs.set("statut", filters.statut);
            const q = qs.toString();
            return this.request(`/v1/clients${q ? `?${q}` : ""}`);
        },
        get: (id) => this.request(`/v1/clients/${id}`),
        create: (input) => this.request("/v1/clients", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        update: (id, input) => this.request(`/v1/clients/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        archive: (id) => this.request(`/v1/clients/${id}`, { method: "DELETE" }),
    };
    /**
     * Contrats commerciaux (Vague 1 A.5).
     * Lie un client à un site avec taux horaires et période.
     */
    contrats = {
        list: (filters = {}) => {
            const qs = new URLSearchParams();
            if (filters.statut)
                qs.set("statut", filters.statut);
            if (filters.clientId)
                qs.set("clientId", filters.clientId);
            if (filters.siteId)
                qs.set("siteId", filters.siteId);
            const q = qs.toString();
            return this.request(`/v1/contrats${q ? `?${q}` : ""}`);
        },
        get: (id) => this.request(`/v1/contrats/${id}`),
        create: (input) => this.request("/v1/contrats", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        update: (id, input) => this.request(`/v1/contrats/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        archive: (id) => this.request(`/v1/contrats/${id}`, { method: "DELETE" }),
    };
    /**
     * Vacations — missions planifiées (Vague 1 Phase B).
     */
    vacations = {
        list: (filters = {}) => {
            const qs = new URLSearchParams();
            if (filters.from)
                qs.set("from", filters.from);
            if (filters.to)
                qs.set("to", filters.to);
            if (filters.siteId)
                qs.set("siteId", filters.siteId);
            if (filters.agentId)
                qs.set("agentId", filters.agentId);
            if (filters.statut)
                qs.set("statut", filters.statut);
            const q = qs.toString();
            return this.request(`/v1/vacations${q ? `?${q}` : ""}`);
        },
        get: (id) => this.request(`/v1/vacations/${id}`),
        create: (input) => this.request("/v1/vacations", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        update: (id, input) => this.request(`/v1/vacations/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        remove: (id) => this.request(`/v1/vacations/${id}`, { method: "DELETE" }),
        /**
         * B.4 — Diffusion planning. Envoie aux agents affectés un récap email
         * (+ SMS optionnel). Avec `dryRun: true`, simule sans envoyer ni
         * marquer — utile pour le preview UI.
         */
        diffuse: (input = {}) => this.request("/v1/vacations/diffuse", {
            method: "POST",
            body: JSON.stringify(input),
        }),
    };
    /**
     * Self-service (Vague 1 Phase C). Consommé par l'app agent PWA.
     * Tout est scopé au user connecté côté API (pas de tenant param).
     */
    me = {
        getAgent: () => this.request("/v1/me/agent"),
        vacations: (query = {}) => {
            const qs = new URLSearchParams();
            if (query.from)
                qs.set("from", query.from);
            if (query.to)
                qs.set("to", query.to);
            const q = qs.toString();
            return this.request(`/v1/me/vacations${q ? `?${q}` : ""}`);
        },
        /** C.2 — Pointage prise/sortie de poste par l'agent connecté. */
        pointage: (input) => this.request("/v1/me/pointages", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        /** C.2 — Liste les pointages de l'agent sur une vacation. */
        pointages: (vacationId) => this.request(`/v1/me/pointages?vacationId=${encodeURIComponent(vacationId)}`),
        /** C.3.a — Ajout d'une entrée de main courante par l'agent connecté. */
        mainCourante: (input) => this.request("/v1/me/main-courante", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        /** C.3.a — Liste les entrées de main courante de l'agent sur une vacation. */
        mainCouranteList: (vacationId) => this.request(`/v1/me/main-courante?vacationId=${encodeURIComponent(vacationId)}`),
        /** C.3.b — Scan d'un point de ronde par l'agent connecté. */
        scanRonde: (input) => this.request("/v1/me/rondes/scan", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        /** C.3.b — Liste des passages de l'agent sur une vacation. */
        rondes: (vacationId) => this.request(`/v1/me/rondes?vacationId=${encodeURIComponent(vacationId)}`),
    };
    /**
     * Points de ronde — référentiel par site (Vague 1 C.3.b).
     * CRUD superviseur.
     */
    pointsRonde = {
        listForSite: (siteId) => this.request(`/v1/sites/${siteId}/points-ronde`),
        get: (id) => this.request(`/v1/points-ronde/${id}`),
        create: (siteId, input) => this.request(`/v1/sites/${siteId}/points-ronde`, {
            method: "POST",
            body: JSON.stringify(input),
        }),
        update: (id, input) => this.request(`/v1/points-ronde/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        archive: (id) => this.request(`/v1/points-ronde/${id}`, { method: "DELETE" }),
    };
    /**
     * Passages de ronde — vue superviseur (Vague 1 C.3.b).
     * Permission `ronde.read`.
     */
    rondes = {
        list: (query = {}) => {
            const qs = new URLSearchParams();
            if (query.vacationId)
                qs.set("vacationId", query.vacationId);
            if (query.agentId)
                qs.set("agentId", query.agentId);
            if (query.pointRondeId)
                qs.set("pointRondeId", query.pointRondeId);
            if (query.siteId)
                qs.set("siteId", query.siteId);
            if (query.from)
                qs.set("from", query.from);
            if (query.to)
                qs.set("to", query.to);
            const q = qs.toString();
            return this.request(`/v1/rondes${q ? `?${q}` : ""}`);
        },
    };
    /**
     * Main courante — vue superviseur (Vague 1 C.3.a).
     * Lecture cross-agents + vérification d'intégrité de la chaîne hash.
     */
    mainCourante = {
        list: (query = {}) => {
            const qs = new URLSearchParams();
            if (query.vacationId)
                qs.set("vacationId", query.vacationId);
            if (query.agentId)
                qs.set("agentId", query.agentId);
            if (query.siteId)
                qs.set("siteId", query.siteId);
            if (query.from)
                qs.set("from", query.from);
            if (query.to)
                qs.set("to", query.to);
            const q = qs.toString();
            return this.request(`/v1/main-courante${q ? `?${q}` : ""}`);
        },
        verify: (vacationId) => this.request(`/v1/main-courante/verify?vacationId=${encodeURIComponent(vacationId)}`),
    };
    /**
     * Pointages — vue superviseur (Vague 1 C.2).
     * Lecture seule cross-agents, permission `pointage.read`.
     */
    pointages = {
        list: (query = {}) => {
            const qs = new URLSearchParams();
            if (query.vacationId)
                qs.set("vacationId", query.vacationId);
            if (query.agentId)
                qs.set("agentId", query.agentId);
            if (query.from)
                qs.set("from", query.from);
            if (query.to)
                qs.set("to", query.to);
            const q = qs.toString();
            return this.request(`/v1/pointages${q ? `?${q}` : ""}`);
        },
    };
    /**
     * Alertes temps réel (Vague 1 B.3).
     * Scan la fenêtre `from→to` (défaut J → J+30) et retourne les alertes
     * sur les vacations + les qualifs expirant.
     */
    alerts = {
        list: (query = {}) => {
            const qs = new URLSearchParams();
            if (query.from)
                qs.set("from", query.from);
            if (query.to)
                qs.set("to", query.to);
            if (query.agentId)
                qs.set("agentId", query.agentId);
            if (query.siteId)
                qs.set("siteId", query.siteId);
            if (query.minSeverity)
                qs.set("minSeverity", query.minSeverity);
            const q = qs.toString();
            return this.request(`/v1/alerts${q ? `?${q}` : ""}`);
        },
    };
    /**
     * Facturation — D.1 consolidation heures depuis pointages + agrégat.
     * Permission `billing.read`.
     */
    billing = {
        /** Détail d'une vacation (planifiées / réelles / facturables + prix HT). */
        vacationHours: (vacationId, regle) => {
            const q = regle ? `?regle=${regle}` : "";
            return this.request(`/v1/billing/vacations/${encodeURIComponent(vacationId)}/hours${q}`);
        },
        /**
         * Agrégat client × période [× sites] préparant une facture.
         * Retourne totaux par type de période, franchise, TVA, warnings.
         */
        preview: (query) => {
            const qs = new URLSearchParams();
            qs.set("clientId", query.clientId);
            qs.set("from", query.from);
            qs.set("to", query.to);
            if (query.siteIds && query.siteIds.length > 0) {
                qs.set("siteIds", query.siteIds.join(","));
            }
            if (query.regle)
                qs.set("regle", query.regle);
            return this.request(`/v1/billing/preview?${qs.toString()}`);
        },
    };
    /**
     * Factures — D.2 émission inaltérable + chaînage hash.
     * Lifecycle : brouillon → emise → (envoyee_pa → acceptee_pa / refusee_pa)
     *           → payee | en_retard | annulee.
     * Permissions : facture.read / .create / .update / .emit / .cancel / .mark_paid.
     */
    facturesAcompte = {
        list: (query = {}) => {
            const qs = new URLSearchParams();
            if (query.statut)
                qs.set("statut", query.statut);
            if (query.contratId)
                qs.set("contratId", query.contratId);
            if (query.clientId)
                qs.set("clientId", query.clientId);
            if (query.annee != null)
                qs.set("annee", String(query.annee));
            const q = qs.toString();
            return this.request(`/v1/factures-acompte${q ? `?${q}` : ""}`);
        },
        get: (id) => this.request(`/v1/factures-acompte/${id}`),
        create: (input) => this.request("/v1/factures-acompte", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        update: (id, input) => this.request(`/v1/factures-acompte/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        emit: (id) => this.request(`/v1/factures-acompte/${id}/emit`, { method: "POST" }),
        /** Envoie la facture d'acompte au client par email (PDF en PJ). */
        send: (id) => this.request(`/v1/factures-acompte/${id}/send`, { method: "POST" }),
        /** Génère un acompte brouillon depuis un devis. */
        fromDevis: (devisId, input) => this.request(`/v1/factures-acompte/from-devis/${devisId}`, {
            method: "POST",
            body: JSON.stringify(input),
        }),
        encaisser: (id, input) => this.request(`/v1/factures-acompte/${id}/encaisser`, {
            method: "POST",
            body: JSON.stringify(input),
        }),
        annuler: (id, input) => this.request(`/v1/factures-acompte/${id}/annuler`, {
            method: "POST",
            body: JSON.stringify(input),
        }),
        deduire: (id, input) => this.request(`/v1/factures-acompte/${id}/deduire`, {
            method: "POST",
            body: JSON.stringify(input),
        }),
        pdfUrl: (id) => `${this.opts.baseUrl}/v1/factures-acompte/${id}/pdf`,
    };
    factures = {
        list: (query = {}) => {
            const qs = new URLSearchParams();
            if (query.statut)
                qs.set("statut", query.statut);
            if (query.clientId)
                qs.set("clientId", query.clientId);
            if (query.annee != null)
                qs.set("annee", String(query.annee));
            if (query.periodeDu)
                qs.set("periodeDu", query.periodeDu);
            if (query.periodeAu)
                qs.set("periodeAu", query.periodeAu);
            const q = qs.toString();
            return this.request(`/v1/factures${q ? `?${q}` : ""}`);
        },
        get: (id) => this.request(`/v1/factures/${id}`),
        /** Crée un brouillon depuis une preview billing. */
        create: (input) => this.request("/v1/factures", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        /** Crée un brouillon avec lignes saisies directement (vente d'heures). */
        createManual: (input) => this.request("/v1/factures/manual", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        /** Convertit un devis en facture brouillon (reprend ses lignes). */
        fromDevis: (devisId) => this.request(`/v1/factures/from-devis/${devisId}`, { method: "POST" }),
        /** Modifie un brouillon (champs cycle seulement). */
        update: (id, input) => this.request(`/v1/factures/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        /** Supprime un brouillon (refuse une facture émise). */
        remove: (id) => this.request(`/v1/factures/${id}`, { method: "DELETE" }),
        /**
         * Émet la facture : réserve un numéro séquentiel atomique
         * (F-AAAA-MM-NNNN), pose le chaînage hash, snapshote l'émetteur.
         * Irréversible.
         */
        emit: (id, input = {}) => this.request(`/v1/factures/${id}/emit`, {
            method: "POST",
            body: JSON.stringify(input),
        }),
        markPaid: (id, input) => this.request(`/v1/factures/${id}/mark-paid`, {
            method: "POST",
            body: JSON.stringify(input),
        }),
        /**
         * Annule une facture émise en générant un avoir compensatoire.
         * Réponse : `{ facture, avoir }`.
         */
        cancel: (id, input) => this.request(`/v1/factures/${id}/cancel`, {
            method: "POST",
            body: JSON.stringify(input),
        }),
        /** Vérifie l'intégrité du chaînage hash sur les factures émises. */
        verifyChain: () => this.request("/v1/factures/verify"),
        /**
         * Télécharge le PDF Factur-X (PDF/A-3 + XML CII embedded).
         * Cache storage : la première fois génère, ensuite sert le fichier
         * archivé. Refusé pour les brouillons.
         */
        pdf: async (id) => {
            const url = `${this.opts.baseUrl}/v1/factures/${encodeURIComponent(id)}/pdf`;
            const headers = new Headers();
            const token = this.opts.getAuthToken?.();
            if (token)
                headers.set("Authorization", `Bearer ${token}`);
            const fetchImpl = this.opts.fetchFn ?? fetch;
            let response;
            try {
                response = await fetchImpl(url, { credentials: "include", headers });
            }
            catch (cause) {
                throw new ApiTransportError(`Échec réseau sur /factures/${id}/pdf`, cause);
            }
            if (!response.ok) {
                if (response.status === 401)
                    this.opts.onUnauthorized?.();
                throw new ApiClientError("pdf_failed", `Téléchargement PDF impossible (${response.status})`, response.status);
            }
            return response.blob();
        },
        /**
         * Envoie une facture émise à la Plateforme Agréée configurée
         * (IOPOLE en prod, Mock à défaut). Statut → envoyee_pa.
         */
        sendToPa: (id) => this.request(`/v1/factures/${id}/send-pa`, { method: "POST" }),
        /** Envoie la facture émise au client par email (PDF Factur-X en PJ). */
        send: (id) => this.request(`/v1/factures/${id}/send`, { method: "POST" }),
        /** Vue trésorerie agrégée (encours, en retard, à venir 60j, payées mois, annulées). */
        treasury: () => this.request("/v1/factures/treasury"),
    };
    /**
     * Devis — Phase E.
     * Cycle : brouillon → envoye → accepte / refuse / expire / annule.
     * À l'acceptation : génération automatique d'un Contrat lié.
     */
    devis = {
        list: (query = {}) => {
            const qs = new URLSearchParams();
            if (query.statut)
                qs.set("statut", query.statut);
            if (query.clientId)
                qs.set("clientId", query.clientId);
            if (query.annee != null)
                qs.set("annee", String(query.annee));
            const q = qs.toString();
            return this.request(`/v1/devis${q ? `?${q}` : ""}`);
        },
        get: (id) => this.request(`/v1/devis/${id}`),
        create: (input) => this.request("/v1/devis", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        update: (id, input) => this.request(`/v1/devis/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
        }),
        remove: (id) => this.request(`/v1/devis/${id}`, { method: "DELETE" }),
        /**
         * Verrouille, génère PDF, envoie email avec lien public d'acceptation.
         * Statut → envoye.
         */
        send: (id, input = {}) => this.request(`/v1/devis/${id}/send`, {
            method: "POST",
            body: JSON.stringify(input),
        }),
        cancel: (id, input = {}) => this.request(`/v1/devis/${id}/cancel`, {
            method: "POST",
            body: JSON.stringify(input),
        }),
        /** Télécharge le PDF du devis (envoyé ou ultérieur). */
        pdf: async (id) => {
            const url = `${this.opts.baseUrl}/v1/devis/${encodeURIComponent(id)}/pdf`;
            const headers = new Headers();
            const token = this.opts.getAuthToken?.();
            if (token)
                headers.set("Authorization", `Bearer ${token}`);
            const fetchImpl = this.opts.fetchFn ?? fetch;
            let response;
            try {
                response = await fetchImpl(url, { credentials: "include", headers });
            }
            catch (cause) {
                throw new ApiTransportError(`Échec réseau sur /devis/${id}/pdf`, cause);
            }
            if (!response.ok) {
                if (response.status === 401)
                    this.opts.onUnauthorized?.();
                throw new ApiClientError("pdf_failed", `Téléchargement PDF impossible (${response.status})`, response.status);
            }
            return response.blob();
        },
    };
    /**
     * Endpoints publics de signature/acceptation client — pas d'auth, accès
     * par token UUID (envoyé dans le lien email).
     */
    publicDevis = {
        getByToken: (token) => this.request(`/v1/public/devis/${encodeURIComponent(token)}`),
        accept: (token, input) => this.request(`/v1/public/devis/${encodeURIComponent(token)}/accept`, {
            method: "POST",
            body: JSON.stringify(input),
        }),
        pdfUrl: (token) => `${this.opts.baseUrl}/v1/public/devis/${encodeURIComponent(token)}/pdf`,
    };
    /**
     * RGPD (T2.7). Types inline car non encore canonisés dans @victor/types.
     */
    rgpd = {
        listDemandes: () => this.request("/v1/rgpd/demandes"),
        createDemande: (input) => this.request("/v1/rgpd/demandes", {
            method: "POST",
            body: JSON.stringify(input),
        }),
        processDemande: (id) => this.request(`/v1/rgpd/demandes/${id}/process`, { method: "POST" }),
    };
}
export function createVictorApi(opts) {
    return new VictorApi(opts);
}
//# sourceMappingURL=index.js.map